import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Camera, Film, FileText, Newspaper, Mail } from "lucide-react";
import { motion } from "framer-motion";
import { useLanguage } from "@/i18n/LanguageContext";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import Breadcrumbs from "@/components/Breadcrumbs";

export default function Mediatheque() {
 const { locale } = useLanguage();
 const l = (fr, en, pt) => (locale === "en" ? en : locale === "pt" ? pt : fr);

 const sections = [
 {
 icon: Camera,
 key: "photos",
 subject: "Photos",
 label: l("Photothèque", "Photo library", "Fototeca"),
 title: l("Notre banque d'images officielle.", "Our official image library.", "A nossa biblioteca oficial de imagens."),
 body: l(
 "Portraits, produits, terroirs, producteurs, événements, une sélection curatée à disposition de la presse, des partenaires et des institutions.",
 "Portraits, products, terroirs, producers, events, a curated selection available to press, partners and institutions.",
 "Retratos, produtos, terroirs, produtores, eventos, uma selecção curada disponível para imprensa, parceiros e instituições."
 ),
 cta: l("Demander l'accès presse", "Request press access", "Solicitar acesso à imprensa"),
 status: l("Disponible sur demande", "Available on request", "Disponível a pedido"),
 },
 {
 icon: Film,
 key: "videos",
 subject: "Vidéos",
 label: l("Vidéos", "Videos", "Vídeos"),
 title: l("Films, portraits, reportages.", "Films, portraits, reports.", "Filmes, retratos, reportagens."),
 body: l(
 "Nos films racontent les gestes, les récoltes et les visages de la Maison. Une collection en cours de construction, à découvrir prochainement.",
 "Our films tell the gestures, the harvests and the faces of the House. A collection under construction, to be discovered soon.",
 "Os nossos filmes contam os gestos, as colheitas e os rostos da Casa. Uma colecção em construção, a descobrir em breve."
 ),
 cta: l("S'abonner à la lettre", "Subscribe to the letter", "Subscrever a carta"),
 status: l("Prochainement", "Coming soon", "Em breve"),
 },
 {
 icon: FileText,
 key: "catalogues",
 subject: "Catalogue",
 label: l("Catalogues", "Catalogues", "Catálogos"),
 title: l("Nos catalogues produits par marque.", "Our product catalogues by brand.", "Os nossos catálogos de produtos por marca."),
 body: l(
 "NANDA SÁ · NANDA SÁ Cosmétiques · ENERGIA PRO, catalogues distribution, tarifs professionnels, fiches techniques. Réservés aux professionnels après identification.",
 "NANDA SÁ · NANDA SÁ Cosmétiques · ENERGIA PRO, distribution catalogues, professional pricing, technical sheets. Reserved for professionals after identification.",
 "NANDA SÁ · NANDA SÁ Cosmétiques · ENERGIA PRO, catálogos de distribuição, preços profissionais, fichas técnicas. Reservados a profissionais após identificação."
 ),
 cta: l("Demander le catalogue", "Request the catalogue", "Solicitar o catálogo"),
 status: l("Sur demande professionnelle", "Professional request", "A pedido profissional"),
 },
 {
 icon: Newspaper,
 key: "presse",
 subject: "Presse",
 label: l("Presse & Communiqués", "Press & Releases", "Imprensa & Comunicados"),
 title: l("Le kit presse de la Maison.", "The House press kit.", "O kit de imprensa da Casa."),
 body: l(
 "Communiqués officiels, biographies, logos haute définition, éléments de langage, tout le nécessaire pour couvrir NANDA SÁ avec précision.",
 "Official releases, biographies, high-definition logos, key messages, everything needed to cover NANDA SÁ with precision.",
 "Comunicados oficiais, biografias, logótipos em alta definição, elementos de linguagem, tudo o necessário para cobrir a NANDA SÁ com precisão."
 ),
 cta: l("Obtenir le kit presse", "Get the press kit", "Obter o kit de imprensa"),
 status: l("Sur demande", "On request", "A pedido"),
 },
 ];

 return (
 <div data-testid="page-mediatheque">
 <section className="border-b border-nanda-line">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 pt-24 md:pt-40 pb-16 md:pb-24 grid grid-cols-1 md:grid-cols-12 gap-10">
 <FadeUp className="md:col-span-4">
 <Breadcrumbs items={[{ label: l("Médiathèque", "Media library", "Mediateca") }]} className="mb-6" />
 <div className="label-overline text-nanda-muted">{l("Médiathèque", "Media library", "Mediateca")}</div>
 </FadeUp>
 <FadeUp delay={0.1} className="md:col-span-8">
 <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl text-balance leading-[1.0]">
 {l("Images, films, catalogues, presse.", "Images, films, catalogues, press.", "Imagens, filmes, catálogos, imprensa.")}
 </h1>
 <p className="mt-10 max-w-2xl text-nanda-inkSoft text-base md:text-lg leading-relaxed">
 {l(
 "La médiathèque NANDA SÁ regroupe l'ensemble des ressources officielles à disposition des journalistes, distributeurs, institutions et partenaires. Accès sur demande, dans le respect de nos droits d'usage.",
 "The NANDA SÁ media library brings together all the official resources available to journalists, distributors, institutions and partners. Access on request, respecting our usage rights.",
 "A mediateca NANDA SÁ reúne todos os recursos oficiais disponíveis para jornalistas, distribuidores, instituições e parceiros. Acesso a pedido, no respeito pelos nossos direitos de uso."
 )}
 </p>
 </FadeUp>
 </div>
 </section>

 <section className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-20 md:py-28">
 <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-px bg-nanda-line border border-nanda-line">
 {sections.map((s, i) => {
 const Icon = s.icon;
 return (
 <StaggerItem key={s.key} className="bg-nanda-bg p-10 md:p-14 group hover:bg-nanda-bgAlt transition-colors duration-500" data-testid={`media-${s.key}`}>
 <div className="flex items-start justify-between mb-8">
 <div className="text-nanda-ink group-hover:text-nanda-terracotta transition-colors duration-500">
 <Icon size={32} strokeWidth={1.2} />
 </div>
 <div className="label-overline text-nanda-muted num-editorial">0{i + 1}</div>
 </div>
 <div className="label-overline text-nanda-terracotta mb-3">{s.status}</div>
 <div className="label-overline text-nanda-muted mb-2">{s.label}</div>
 <h2 className="font-serif text-3xl md:text-4xl leading-tight text-nanda-ink mb-6">{s.title}</h2>
 <div className="rule-gold w-24 mb-8" />
 <p className="text-nanda-inkSoft text-[15px] md:text-base leading-relaxed mb-10">{s.body}</p>
 <Link
 to={`/contact?subject=${encodeURIComponent(s.subject)}`}
 data-testid={`media-cta-${s.key}`}
 className="btn-arrow inline-flex items-center gap-3 label-overline text-nanda-ink border-b border-nanda-ink pb-1 hover:text-nanda-terracotta hover:border-nanda-terracotta transition-colors duration-500"
 >
 {s.cta} <ArrowUpRight size={14} strokeWidth={1.5} />
 </Link>
 </StaggerItem>
 );
 })}
 </Stagger>
 </section>

 <section className="border-t border-nanda-line bg-nanda-bgAlt">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10">
 <FadeUp className="md:col-span-7">
 <div className="label-overline text-nanda-muted mb-4">{l("Presse", "Press", "Imprensa")}</div>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-nanda-ink leading-[1.05]">
 {l("Une demande éditoriale ?", "An editorial request?", "Um pedido editorial?")}
 </h2>
 <p className="mt-8 max-w-xl text-nanda-inkSoft text-base md:text-[17px] leading-relaxed">
 {l(
 "Interviews, portraits, visites de sites, participation à vos numéros spéciaux, notre équipe communication répond dans un délai de 48 heures.",
 "Interviews, portraits, site visits, contributions to your special issues, our communications team responds within 48 hours.",
 "Entrevistas, retratos, visitas a locais, participação nas suas edições especiais, a nossa equipa de comunicação responde no prazo de 48 horas."
 )}
 </p>
 </FadeUp>
 <FadeUp delay={0.15} className="md:col-span-5 flex md:justify-end items-end">
 <motion.div>
 <Link
 to="/contact?subject=Presse"
 data-testid="media-press-cta"
 className="btn-arrow inline-flex items-center gap-3 bg-nanda-terracotta text-white px-7 py-4 hover:bg-[#8F5B3F] transition-colors duration-500 label-overline"
 >
 <Mail size={14} strokeWidth={1.5} />
 {l("Contact presse", "Press contact", "Contacto de imprensa")}
 <ArrowUpRight size={16} strokeWidth={1.5} />
 </Link>
 </motion.div>
 </FadeUp>
 </div>
 </section>
 </div>
 );
}
